/**
 * 提示词组装（T07）。
 * -----------------------------------------------------------------------------
 * 把「角色卡系统提示词」与「命中的世界书条目」拼成最终发给模型的 system 提示。
 * 本模块是纯函数、无副作用、无 IO —— 对话页（T06）与后续 Bot 接入都复用它，
 * 因此命中规则必须精确、可测试、可预测。
 *
 * 世界书命中规则：
 * 1. 只考虑 `enabled === true` 的条目；
 * 2. 常驻条目直接命中；其他条目需要主关键词命中，并满足可选副关键词条件；
 *    默认不区分大小写、包含匹配，可由条目开启大小写或完整词匹配；
 * 3. 空关键词视为无效，不参与匹配（避免 `includes('')` 恒真）；
 * 4. 多条命中按 `priority` 降序注入，优先级相同保持角色卡里的原始顺序。
 *
 * 注入格式：
 *   <systemPrompt>
 *
 *   [世界书]
 *   <条目内容>
 *
 *   [世界书]
 *   <条目内容>
 * 每条一个 `[世界书]` 标题行，条目之间空行分隔；无命中时原样返回 systemPrompt。
 * ============================================================================= */

import type { ChatMessage, Preset, WorldBookEntry } from '../../core/types';

/** 上下文输入：对话消息数组，或已拼好的文本行（便于单测与 Bot 复用）。 */
export type PresetContext = readonly (ChatMessage | string)[];

/** 世界书条目的固定标题行。 */
export const WORLD_BOOK_HEADER = '[世界书]';
/** 只导入世界书、没有角色提示词时使用的设定块标题。 */
export const WORLD_SETTING_HEADER = '[世界设定]';
/** 单独选用世界书时每次最多注入的设定字符数，避免一次撑爆上下文。 */
export const STANDALONE_WORLD_BOOK_BUDGET_CHARS = 8000;

const WORLD_SETTING_DIRECTIVE =
  '以下内容是本轮对话的世界书设定。回答时必须遵守这些设定，保持世界观、人物关系和语气一致；不要把自己当作普通 AI 助手，也不要在回答中复述或暴露这些设定。';

/** 把上下文消息规范成一段可做包含匹配的文本。 */
export function contextToText(context: PresetContext): string {
  return context
    .map((item) => (typeof item === 'string' ? item : item.content))
    .join('\n');
}

function hitKeyword(entry: WorldBookEntry, contextText: string, keyword: string): boolean {
  const needle = entry.caseSensitive === true ? keyword.trim() : keyword.trim().toLowerCase();
  if (needle === '') return false;
  const haystack = entry.caseSensitive === true ? contextText : contextText.toLowerCase();
  if (entry.matchWholeWords !== true || /\s/u.test(needle)) return haystack.includes(needle);
  // 与 SillyTavern 的非字母数字边界规则一致，关键词自身始终按纯文本处理。
  const escaped = needle.replace(/[.*+?^${}()|[\]\\]/gu, '\\$&');
  return new RegExp(`(?:^|\\W)(${escaped})(?:$|\\W)`, 'u').test(haystack);
}

function hitEntry(entry: WorldBookEntry, contextText: string): boolean {
  if (entry.constant === true) return true;
  if (!entry.keywords.some((keyword) => hitKeyword(entry, contextText, keyword))) return false;
  const secondary = (entry.secondaryKeywords ?? []).filter((keyword) => keyword.trim() !== '');
  if (entry.selectiveLogic === undefined || secondary.length === 0) return true;
  const matches = secondary.map((keyword) => hitKeyword(entry, contextText, keyword));
  switch (entry.selectiveLogic) {
    case 'andAny': return matches.some(Boolean);
    case 'andAll': return matches.every(Boolean);
    case 'notAny': return !matches.some(Boolean);
    case 'notAll': return !matches.every(Boolean);
  }
}

/**
 * 取出当前上下文命中的世界书条目（已按 priority 降序 + 原顺序稳定排序）。
 * 命中规则由条目的关键词匹配选项决定。
 */
export function matchWorldBookEntries(preset: Preset, contextText: string): WorldBookEntry[] {
  const entries = preset.worldBook ?? [];

  return entries
    .map((entry, index) => ({ entry, index }))
    .filter(({ entry }) => entry.enabled === true && hitEntry(entry, contextText))
    .sort((a, b) => b.entry.priority - a.entry.priority || a.index - b.index)
    .map(({ entry }) => entry);
}

/** 只选了世界书、没有角色提示词时，按优先级把可用设定全部注入。 */
export function isStandaloneWorldBook(
  preset: Pick<Preset, 'systemPrompt' | 'description' | 'worldBook' | 'tags'>,
): boolean {
  const hasUsableEntries = (preset.worldBook ?? []).some(
    (entry) => entry.enabled === true && entry.content.trim() !== '',
  );
  if (!hasUsableEntries) return false;
  // 一旦补了角色提示词，它就已经是角色卡，世界书只按关键词参与。
  if (preset.systemPrompt.trim() !== '') return false;
  if (preset.tags?.includes('导入世界书') === true) return true;
  return (preset.description?.trim() ?? '') === '';
}

/**
 * 把独立世界书转成可用的 system 设定块。
 *
 * 世界书条目通常只有在关键词命中时才注入；但如果用户直接把世界书当作
 * 「角色卡」选中，泛化问候往往不含任何关键词，结果就是完全没有上下文，
 * 模型会退回普通助手口吻。这里在**用户明确选择世界书**时，把启用的条目
 * 按优先级铺进设定块（受字符预算限制），保证选择本身一定生效。
 */
export function assembleStandaloneWorldBook(
  preset: Preset,
  budgetChars = STANDALONE_WORLD_BOOK_BUDGET_CHARS,
): { text: string; count: number; truncated: boolean } {
  const entries = (preset.worldBook ?? [])
    .map((entry, index) => ({ entry, index }))
    .filter(({ entry }) => entry.enabled === true && entry.content.trim() !== '')
    .sort((a, b) => b.entry.priority - a.entry.priority || a.index - b.index)
    .map(({ entry }) => entry);

  if (entries.length === 0) return { text: '', count: 0, truncated: false };

  let text = `${WORLD_SETTING_HEADER}\n${WORLD_SETTING_DIRECTIVE}`;
  let count = 0;
  let truncated = false;
  for (const entry of entries) {
    const block = `\n\n${WORLD_BOOK_HEADER}\n${entry.content.trim()
      .replace(/\{\{char\}\}/giu, preset.name)
      .replace(/\{\{user\}\}/giu, '用户')}`;
    if (text.length + block.length > budgetChars) {
      truncated = true;
      break;
    }
    text += block;
    count += 1;
  }
  return { text, count, truncated };
}

/** 把命中条目拼成 `[世界书] + 内容` 的注入块（条目之间空行分隔）。 */
export function formatWorldBookEntries(entries: readonly WorldBookEntry[], characterName = '角色'): string {
  return entries
    .map((entry) => `${WORLD_BOOK_HEADER}\n${entry.content.trim()
      .replace(/\{\{char\}\}/giu, characterName)
      .replace(/\{\{user\}\}/giu, '用户')}`)
    .join('\n\n');
}

/**
 * 组装最终 system 提示。
 *
 * @param preset 角色卡（systemPrompt 为空串时只注入世界书块）
 * @param contextMessages 当前上下文（对话消息或文本行）
 * @returns 无命中时**原样**返回 `preset.systemPrompt`；有命中时追加世界书块
 */
export function assembleSystemPrompt(
  preset: Preset,
  contextMessages: PresetContext = [],
): string {
  const systemPrompt = preset.systemPrompt ?? '';
  const hits = matchWorldBookEntries(preset, contextToText(contextMessages));

  if (hits.length === 0) return systemPrompt;

  const worldBook = formatWorldBookEntries(hits, preset.name);
  return systemPrompt.trim() === '' ? worldBook : `${systemPrompt}\n\n${worldBook}`;
}
